import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Beacon, BeaconTableColumns } from "../../../api/models";
import { PagedApiState } from "../../../api/useApiState";
import { BeaconTableBase } from "../../../components/BeaconTableBase";
import { AssignBeaconsModalConstructionSite } from "../../../components/configuration/AssignBeaconsModal";
import { BeaconConfigModalBulk } from "../../../components/configuration/BeaconConfigModalBulk";
import { UnassignBeaconsButtonSite } from "../../../components/configuration/UnassignBeaconsButton";
import { useSetting } from "../../../util";
import { BeaconsWithProjectSite } from "../../../screens/construction-project/ConstructionProjectWrapper";

interface BeaconDetailsProps {
  constructionSiteId: string;
  siteBeacons: BeaconsWithProjectSite[] | undefined;
  organizationId?: string;
}

const useSelectedBeacons = () => {
  const [selectedBeaconIds, setSelectedBeaconIds] = useState<string[]>([]);

  const onSelect = (selectedRowKeys: string[]) => {
    setSelectedBeaconIds(selectedRowKeys);
  };

  const clearSelectedBeaconIds = () => {
    setSelectedBeaconIds([]);
  };

  return { selectedBeaconIds, onSelect, clearSelectedBeaconIds };
};

export const BeaconDetails = ({
  constructionSiteId,
  siteBeacons,
  organizationId,
}: BeaconDetailsProps) => {
  const { t } = useTranslation();

  const { selectedBeaconIds, onSelect, clearSelectedBeaconIds } =
    useSelectedBeacons();

  return (
    <div>
      <div className="sub-title-container">
        {t("construction-site:beacons-in-site")}
      </div>
      <BeaconTableBase
        beaconData={PagedState(siteBeacons) as PagedApiState<Beacon>}
        visibleColumns={[
          BeaconTableColumns.Serial,
          BeaconTableColumns.Status,
          BeaconTableColumns.ConfigurationButton,
        ]}
        showPagination={true}
        rowsSelectable={true}
        onSelect={onSelect}
        showFooter={true}
        tableHeightOffset="450px"
        showSorter={false}
        footerContent={
          <div className="footer-content">
            <>
              <AssignBeaconsModalConstructionSite
                constructionSiteId={constructionSiteId}
                siteBeacons={siteBeacons?.map((beacon) => beacon.serial) || []}
                organizationId={organizationId || ""}
              />

              <UnassignBeaconsButtonSite
                constructionSiteId={constructionSiteId}
                selectedBeaconIds={selectedBeaconIds}
                siteBeacons={siteBeacons?.map((beacon) => beacon.serial) || []}
                onClearSelectedBeaconIds={clearSelectedBeaconIds}
              />
              <BeaconConfigModalBulk
                selectedBeaconSerials={selectedBeaconIds}
              />
            </>
          </div>
        }
      />
    </div>
  );
};

const PagedState = (beacons: BeaconsWithProjectSite[] | undefined) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useSetting(
    "default-page-size",
    50
  );

  return {
    currentPage,
    currentPageSize: currentPageSize,
    goToPage: (p: any) => setCurrentPage(p),
    setPageSize: (pageSize: any) => setCurrentPageSize(pageSize),
    value: {
      items: beacons as unknown as Beacon[],
      totalCount: beacons?.length || 0,
    },
    loading: false,
    failed: false,
    lastLoadedAt: new Date(),
    refresh: () => {},
  };
};
